import { useEffect, useState, useRef } from "react";
import { Check } from "lucide-react";

/**
 * SectionNav — sticky horizontal nav for single-page forms.
 *
 * Scroll-spy: uses IntersectionObserver to track which `<section id={s.id}>`
 * is in view, then highlights it in the nav. Clicking a chip smoothly
 * scrolls to that section.
 *
 * Each section also gets a "completed" check icon when `completedIds` includes
 * it (the parent owns completion logic).
 *
 * Props:
 *   sections:    [{ id: "about", label: "About you" }, ...]
 *   completedIds: Set<string> — sections that are valid/complete
 *   offset:      px offset to account for the sticky nav itself when scrolling
 */
export const SectionNav = ({
  sections = [],
  completedIds = new Set(),
  offset = 100,
}) => {
  const [activeId, setActiveId] = useState(sections[0]?.id || "");
  const observerRef = useRef(null);

  useEffect(() => {
    const elements = sections
      .map((s) => document.getElementById(s.id))
      .filter(Boolean);

    if (!elements.length) return;

    observerRef.current?.disconnect();
    observerRef.current = new IntersectionObserver(
      (entries) => {
        // Pick the topmost section that's intersecting the viewport.
        const visible = entries
          .filter((e) => e.isIntersecting)
          .sort((a, b) => a.boundingClientRect.top - b.boundingClientRect.top);
        if (visible[0]) setActiveId(visible[0].target.id);
      },
      {
        rootMargin: `-${offset + 40}px 0px -55% 0px`,
        threshold: 0,
      }
    );

    elements.forEach((el) => observerRef.current.observe(el));
    return () => observerRef.current?.disconnect();
  }, [sections, offset]);

  const scrollTo = (id) => {
    const el = document.getElementById(id);
    if (!el) return;
    const top = el.getBoundingClientRect().top + window.scrollY - offset;
    window.scrollTo({ top, behavior: "smooth" });
  };

  return (
    <nav
      className="flex items-center gap-1.5 overflow-x-auto -mx-2 px-2 py-1 scrollbar-hide"
      aria-label="Form sections"
      data-testid="section-nav"
    >
      {sections.map((s, i) => {
        const isActive = s.id === activeId;
        const isComplete = completedIds.has(s.id);
        return (
          <button
            key={s.id}
            type="button"
            onClick={() => scrollTo(s.id)}
            className={`inline-flex items-center gap-2 px-3.5 py-2 rounded-full whitespace-nowrap text-sm font-medium transition-all ${
              isActive
                ? "bg-[var(--charcoal)] text-white"
                : isComplete
                ? "bg-[var(--lime)]/10 text-[var(--charcoal)] hover:bg-[var(--lime)]/20"
                : "bg-transparent text-[var(--text-muted)] hover:bg-[var(--bone-soft)]"
            }`}
            data-testid={`section-nav-${s.id}`}
            aria-current={isActive ? "true" : undefined}
          >
            <span
              className={`flex h-5 w-5 items-center justify-center rounded-full text-[10px] font-bold transition-all ${
                isComplete
                  ? "bg-[var(--lime)] text-[var(--dark-bg)]"
                  : isActive
                  ? "bg-white text-[var(--charcoal)]"
                  : "bg-[var(--light-border)] text-[var(--text-muted)]"
              }`}
            >
              {isComplete ? <Check size={11} strokeWidth={3.5} /> : i + 1}
            </span>
            {s.label}
          </button>
        );
      })}
    </nav>
  );
};

export default SectionNav;
